/**
 * Bill of Lading Clauses Fields Component
 *
 * Two sections:
 * 1. clauses[] — dynamic array with clauseType (select) and clauseText (textarea)
 * 2. amendments[] — dynamic array with version, date, reason, amendedFields.
 *    Amendments are typically system-generated, so they are displayed read-only
 *    even in edit mode. The user can delete them but not freely edit them.
 *
 * Clause types cover the most common B/L clause categories.
 */

import { useFieldArray } from 'react-hook-form';
import Input from '../common/Input';
import Button from '../common/Button';
import { getFieldIssues } from '../../utils/confidenceUtils';

const CLAUSE_TYPE_OPTIONS = [
  { value: '', label: 'Select...' },
  { value: 'GENERAL', label: 'General' },
  { value: 'FREIGHT', label: 'Freight' },
  { value: 'SHIPPER', label: 'Shipper' },
  { value: 'CARRIER_LIABILITY', label: 'Carrier Liability' },
  { value: 'JURISDICTION', label: 'Jurisdiction / Law' },
  { value: 'DANGEROUS_GOODS', label: 'Dangerous Goods' },
  { value: 'TEMPERATURE', label: 'Temperature / Reefer' },
  { value: 'CUSTOMS', label: 'Customs' },
  { value: 'OTHER', label: 'Other' }
];

/**
 * BLClausesFields Component
 *
 * @param {Object} props
 * @param {Object} props.control - react-hook-form control
 * @param {Function} props.register - react-hook-form register
 * @param {Function} props.watch - react-hook-form watch
 * @param {boolean} props.isEditMode - Whether fields are editable
 * @param {Array} props.issues - Unified issues array
 */
function BLClausesFields({ control, register, watch, isEditMode, issues = [] }) {
  // Dynamic clauses array
  const {
    fields: clauseFields,
    append: appendClause,
    remove: removeClause
  } = useFieldArray({ control, name: 'clauses' });

  // Amendments array — editable for add/remove, but individual fields are read-only
  // (amendments are typically system-generated records of what changed)
  const {
    fields: amendmentFields,
    append: appendAmendment,
    remove: removeAmendment
  } = useFieldArray({ control, name: 'amendments' });

  const addClause = () => {
    appendClause({ clauseType: '', clauseText: '' });
  };

  const addAmendment = () => {
    appendAmendment({
      version: '',
      date: '',
      reason: '',
      amendedFields: ''
    });
  };

  return (
    <div className="space-y-8">
      {/* ---- Section 1: Clauses ---- */}
      <div className="space-y-4">
        <div className="flex items-center justify-between border-b pb-2">
          <h3 className="text-lg font-medium text-gray-900">Clauses</h3>
          {isEditMode && (
            <Button variant="secondary" size="sm" onClick={addClause}>
              + Add Clause
            </Button>
          )}
        </div>

        {clauseFields.length === 0 && (
          <div className="text-center py-4 text-gray-500 bg-gray-50 rounded-md">
            No clauses added. Click "Add Clause" to add a B/L clause.
          </div>
        )}

        {clauseFields.map((field, index) => (
          <div key={field.id} className="border border-gray-200 rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">Clause #{index + 1}</span>
              {isEditMode && (
                <button
                  type="button"
                  onClick={() => removeClause(index)}
                  className="text-red-500 hover:text-red-700 text-sm"
                >
                  Remove
                </button>
              )}
            </div>

            {/* Clause Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Clause Type</label>
              <select
                {...register(`clauses.${index}.clauseType`)}
                disabled={!isEditMode}
                className={`w-full md:w-64 px-3 py-2 border rounded-md shadow-sm text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300 ${!isEditMode ? 'bg-gray-50 text-gray-600' : ''}`}
              >
                {CLAUSE_TYPE_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>

            {/* Clause Text — full-width textarea */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Clause Text</label>
              <textarea
                {...register(`clauses.${index}.clauseText`)}
                disabled={!isEditMode}
                rows={4}
                className={`
                  w-full px-3 py-2 border rounded-md shadow-sm text-sm
                  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                  ${getFieldIssues(issues, `clauses[${index}].clauseText`).length > 0 ? 'border-amber-400 bg-amber-50' : 'border-gray-300'}
                  ${!isEditMode ? 'bg-gray-50 text-gray-600' : ''}
                `}
                placeholder="Enter clause text..."
              />
            </div>
          </div>
        ))}
      </div>

      {/* ---- Section 2: Amendments ---- */}
      <div className="space-y-4">
        <div className="flex items-center justify-between border-b pb-2">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Amendments</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Amendment records are typically system-generated. Individual fields are read-only.
            </p>
          </div>
          {isEditMode && (
            <Button variant="secondary" size="sm" onClick={addAmendment}>
              + Add Amendment
            </Button>
          )}
        </div>

        {amendmentFields.length === 0 && (
          <div className="text-center py-4 text-gray-500 bg-gray-50 rounded-md">
            No amendments recorded.
          </div>
        )}

        {amendmentFields.map((field, index) => (
          <div key={field.id} className="border border-blue-100 bg-blue-50 rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-blue-700">Amendment #{index + 1}</span>
              {isEditMode && (
                <button
                  type="button"
                  onClick={() => removeAmendment(index)}
                  className="text-red-500 hover:text-red-700 text-sm"
                >
                  Remove
                </button>
              )}
            </div>

            {/* Amendment fields — rendered read-only even in edit mode (system records) */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              <Input
                label="Version"
                {...register(`amendments.${index}.version`)}
                disabled={true}
                issues={getFieldIssues(issues, `amendments[${index}].version`)}
              />

              <Input
                label="Date"
                type="date"
                {...register(`amendments.${index}.date`)}
                disabled={true}
                issues={getFieldIssues(issues, `amendments[${index}].date`)}
              />

              <div className="md:col-span-2">
                <Input
                  label="Reason"
                  {...register(`amendments.${index}.reason`)}
                  disabled={true}
                  issues={getFieldIssues(issues, `amendments[${index}].reason`)}
                />
              </div>

              {/* Amended Fields — comma-separated list */}
              <div className="md:col-span-2 lg:col-span-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Amended Fields (comma-separated)
                </label>
                <input
                  {...register(`amendments.${index}.amendedFields`)}
                  disabled={true}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm bg-gray-50 text-gray-600"
                  placeholder="e.g., header.issueDate, parties[0].name"
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default BLClausesFields;
